import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Documentation.css';

const SECTIONS = [
  { id: 'priority', label: 'Platform Priority' },
  { id: 'philosophy', label: 'Core Philosophy' },
  { id: 'step-1', label: 'Step 1: Submission Intake' },
  { id: 'step-2', label: 'Step 2: Underwriting Canvas' },
  { id: 'step-3', label: 'Step 3: Approvals' },
  { id: 'step-4', label: 'Step 4: Leadership Oversight' },
  { id: 'step-5', label: 'Step 5: Broker Delivery' },
  { id: 'open-items', label: 'Open Items' },
];

export function Documentation() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observers = [];

    SECTIONS.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveId(id);
          }
        },
        { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
      );

      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const ref = (id) => (el) => {
    sectionRefs.current[id] = el;
  };

  return (
    <div className="docs">
      {/* Sticky sidebar TOC */}
      <nav className="docs__toc" aria-label="Table of contents">
        <div className="docs__toc-inner">
          <p className="docs__toc-title">Contents</p>
          <ul className="docs__toc-list">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className={`docs__toc-link${activeId === s.id ? ' docs__toc-link--active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    sectionRefs.current[s.id]?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Main content */}
      <div className="docs__content">
        {/* Hero */}
        <div className="docs__hero card">
          <p className="docs__hero-eyebrow">Platform Documentation</p>
          <h1 className="docs__hero-title">DEX Engine</h1>
          <p className="docs__hero-subtitle">
            An underwriting-first operating system for MGAs. This guide explains how
            the platform works — from submission intake through broker delivery.
          </p>
        </div>

        {/* Platform Priority */}
        <section className="docs__section" id="priority" ref={ref('priority')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <h2 className="docs__section-title">Platform Priority</h2>
            </div>
            <p className="docs__section-desc">
              The DEX Engine is optimized in the following order. All workflows,
              data structures, and interfaces reflect this ordering.
            </p>
            <ol className="docs__priority-list">
              <li>
                <strong>Underwriters and their ideas</strong> — the platform exists
                to capture, structure, and scale human underwriting judgment.
              </li>
              <li>
                <strong>MGA leadership and capacity providers</strong> — oversight,
                portfolio visibility, and predictable behavior without friction.
              </li>
              <li>
                <strong>Brokers, both wholesale and retail</strong> — decisive
                answers, clear rationale, and clean quotes.
              </li>
            </ol>
          </div>
        </section>

        {/* Core Philosophy */}
        <section className="docs__section" id="philosophy" ref={ref('philosophy')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <h2 className="docs__section-title">Core Philosophy</h2>
            </div>
            <p className="docs__section-desc">
              DEX Engine is an underwriting-first platform designed to capture,
              structure, and scale human judgment. The Engine is the single operating
              system for the MGA.
            </p>
            <p className="docs__section-desc">
              Over time, it will handle underwriting, distribution management,
              policy administration, accounting and finance, and claims. At this
              stage, the focus is deliberately on underwriting flow, leadership and
              capacity oversight, and broker interaction.
            </p>
            <div className="docs__principle">
              The system does not replace underwriters. It prepares risks, enforces
              discipline, and creates visibility so strong underwriting judgment can
              scale.
            </div>
          </div>
        </section>

        {/* Step 1: Submission Intake */}
        <section className="docs__section" id="step-1" ref={ref('step-1')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <span className="docs__section-step">1</span>
              <h2 className="docs__section-title">Submission Intake</h2>
            </div>
            <p className="docs__section-desc">
              Submissions are treated as underwriting ideas that need structure, not
              as static forms.
            </p>
            <ul className="docs__list">
              <li>Brokers submit risks via email or document upload</li>
              <li>The MGA maintains its own application per product</li>
              <li>
                External applications are ingested and mapped into product-specific
                MGA applications
              </li>
              <li>
                The Engine automatically structures data from broker submissions
              </li>
              <li>A distribution layer tracks wholesale and retail brokers</li>
              <li>
                Each submission generates a unique account page under the submitting
                broker
              </li>
              <li>
                Submissions are routed to underwriters based on real-time workload
                capacity
              </li>
              <li>
                Auto-bind submissions (9-10 risk tier) don't count toward workload
              </li>
              <li>
                Broker ownership is driven by submission, not by BOR or AOR
              </li>
              <li>
                BOR/AOR are tracked as attributes and historical data points
              </li>
              <li>
                Missing data is flagged but does not block underwriting visibility
              </li>
              <li>Underwriters can engage with imperfect submissions early</li>
            </ul>
            <div className="docs__principle">
              Key principle: "Underwriters can see imperfect risks early."
            </div>
          </div>
        </section>

        {/* Step 2: Underwriting Canvas */}
        <section className="docs__section" id="step-2" ref={ref('step-2')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <span className="docs__section-step">2</span>
              <h2 className="docs__section-title">Underwriting Canvas</h2>
            </div>
            <p className="docs__section-desc">
              Before an underwriter engages, the Engine frames the risk.
            </p>

            <p className="docs__section-desc" style={{ fontWeight: 600 }}>
              Risk and alert framework:
            </p>
            <div className="docs__risk-tiers">
              <span className="badge badge--red">0 – 2 Red</span>
              <span className="badge badge--orange">3 – 5 Orange</span>
              <span className="badge badge--yellow">6 – 8 Yellow</span>
              <span className="badge badge--green">9 – 10 Green</span>
            </div>

            <ul className="docs__list">
              <li>
                Framework is consistent across the Engine but each product has its
                own guidelines
              </li>
              <li>Submissions can be re-evaluated across products</li>
              <li>
                Each account includes a dedicated underwriting page showing
                disqualifiers and risk profile
              </li>
              <li>Coverage comparisons if existing policies are provided</li>
              <li>Risk drivers highlighted with expandable detail</li>
              <li>Confidence levels displayed on extracted data</li>
              <li>
                Similar past risks surfaced for higher-risk submissions (0-5)
              </li>
            </ul>
            <div className="docs__principle">
              Key principle: "The system frames the problem, not the answer."
            </div>
          </div>
        </section>

        {/* Step 3: Approvals (deferred) */}
        <section className="docs__section" id="step-3" ref={ref('step-3')}>
          <div className="docs__section-card docs__deferred">
            <div className="docs__section-header" style={{ justifyContent: 'center' }}>
              <span className="docs__section-step">3</span>
              <h2 className="docs__section-title">Approvals</h2>
            </div>
            <div className="docs__deferred-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="18" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeDasharray="5 3" />
                <path d="M14 20h12M20 14v12" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <p className="docs__deferred-text">
              Intentionally deferred pending discussion with cofounder. This step
              will cover underwriter discretion, approval boundaries, authority
              levels, and automation thresholds.
            </p>
            <Link to="/approvals" className="docs__deferred-link">
              View Approvals page &rarr;
            </Link>
          </div>
        </section>

        {/* Step 4: Leadership Oversight */}
        <section className="docs__section" id="step-4" ref={ref('step-4')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <span className="docs__section-step">4</span>
              <h2 className="docs__section-title">Leadership Oversight</h2>
            </div>
            <p className="docs__section-desc">
              Leadership and capacity oversight is passive, not intrusive.
            </p>
            <ul className="docs__list">
              <li>
                Real-time visibility into quotes, claims insights, overrides, risk
                accumulation
              </li>
              <li>Capacity-specific dashboards customized per provider</li>
              <li>
                Underwriters not interrupted unless thresholds are crossed
              </li>
              <li>
                Capacity sees consistent structure, explainable decisions,
                predictable portfolio behavior
              </li>
            </ul>
            <div className="docs__principle">
              Key principle: "Oversight without friction."
            </div>
          </div>
        </section>

        {/* Step 5: Broker Delivery */}
        <section className="docs__section" id="step-5" ref={ref('step-5')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <span className="docs__section-step">5</span>
              <h2 className="docs__section-title">Broker Delivery</h2>
            </div>
            <p className="docs__section-desc">
              The broker experience is intentionally minimal. By the time a broker
              hears back: the decision is decisive, the rationale is clear, the
              quote is clean, the next step is obvious.
            </p>
            <ul className="docs__list">
              <li>
                Brokers primarily see quote progress via color-coded bar
              </li>
              <li>Limited tabs: form comparisons, marketing materials</li>
              <li>Brokers do not see internal underwriting mechanics</li>
            </ul>
            <div className="docs__principle">
              Key principle: "Complexity stays inside the Engine."
            </div>
          </div>
        </section>

        {/* Open Items */}
        <section className="docs__section" id="open-items" ref={ref('open-items')}>
          <div className="docs__section-card">
            <div className="docs__section-header">
              <h2 className="docs__section-title">Open Items</h2>
            </div>
            <p className="docs__section-desc">
              The following areas are under active discussion and will be addressed
              in upcoming releases.
            </p>
            <ul className="docs__open-items">
              <li className="docs__open-item">
                Underwriter discretion and approval boundaries (Step 3)
              </li>
              <li className="docs__open-item">
                Auto-bind thresholds by product
              </li>
              <li className="docs__open-item">
                Broker and account movement data surfacing
              </li>
              <li className="docs__open-item">
                Claims insights depth for capacity providers
              </li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
